import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDatePipe,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxModalService,
} from '@onyx/angular';
import { DateTime, Duration } from 'luxon';
import { catchError, of, Subject, switchMap, tap } from 'rxjs';
import { DriversTachographComponent } from '../../../../../common/components/drivers-tachograph/drivers-tachograph.component';
import { ModalDelegationComponent } from '../../../../../common/components/modals/modal-delegation/modal-delegation.component';
import { ModalLoadingSectionComponent } from '../../../../../common/components/modals/modal-loading-section/modal-loading-section.component';
import { ModalOrderComponent } from '../../../../../common/components/modals/modal-order/modal-order.component';
import { NoActiveOrderComponent } from '../../../../../common/components/modals/modal-order/no-active-order/no-active-order.component';
import { ModalSwapComponent } from '../../../../../common/components/modals/modal-swap/modal-swap.component';
import { ModalVehiclesComponent } from '../../../../../common/components/modals/modal-vehicles/modal-vehicles.component';
import { DurationPipe } from '../../../../../common/pipes/duration.pipe';
import { DelegationHelper } from '../../../../delegations/common/helpers/delegation.helper';
import type { Fleet } from '../../../../fleet/common/interfaces/fleet';
import type { Order } from '../../../../orders/common/interfaces/order';
import { OrdersService } from '../../../../orders/common/services/orders.service';
import type { DriverAssignedVehicleModalData } from '../../../common/components/driver-assigned-vehicle-modal/driver-assigned-vehicle-modal.component';
import { DriverAssignedVehicleModalComponent } from '../../../common/components/driver-assigned-vehicle-modal/driver-assigned-vehicle-modal.component';
import type { Driver } from '../../../common/interfaces/driver';

@Component({
  selector: 'app-driver-modal-summary',
  imports: [
    ModalDelegationComponent,
    ModalOrderComponent,
    ModalVehiclesComponent,
    forwardRef(() => DriversTachographComponent),
    TranslatePipe,
    ModalLoadingSectionComponent,
    OnyxInformationListComponent,
    OnyxDatePipe,
    DurationPipe,
    OnyxInformationRowComponent,
    NoActiveOrderComponent,
    ModalSwapComponent,
  ],
  templateUrl: './driver-modal-summary.component.html',
  styleUrl: './driver-modal-summary.component.scss',
})
export class DriverModalSummaryComponent implements OnInit {
  private readonly ordersService = inject(OrdersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly modalService = inject(OnyxModalService);

  public readonly driver = input.required<Driver>();
  public readonly vehicle = input.required<Fleet | null>();
  public readonly vehicleError = model.required<boolean>();
  public readonly vehicleLoading = input.required<boolean>();
  public readonly close$ = input.required<Subject<void>>();

  protected readonly reloadVehicle = output<void>();

  protected readonly I18N = 'drivers.driverModal';

  protected readonly Duration = Duration;

  protected order = signal<Order | null>(null);
  protected orderError = signal(false);
  protected orderLoading = signal(false);
  protected order$ = new Subject<void>();

  protected timeSinceLastSignal = computed(() => {
    const updatedAt = this.driver().tachograph?.updatedAt;
    if (!updatedAt) return 0;

    return DateTime.now().diff(DateTime.fromISO(updatedAt)).as('seconds');
  });

  protected nextEstimatedContinuousRestAt = computed(() => {
    const tachograph = this.driver().tachograph;
    if (!tachograph) return null;

    const { continuousDriving, dailyDriving, updatedAt } = tachograph;

    if (this.timeSinceLastSignal() > continuousDriving.effectiveRemaining) {
      return null;
    }

    if (dailyDriving.effectiveRemaining < continuousDriving.limit) return null;

    return DateTime.fromISO(updatedAt).plus({
      seconds: continuousDriving.effectiveRemaining,
    });
  });

  protected hasOutdatedDailyDrivingData = computed(() => {
    const tachograph = this.driver().tachograph;
    if (!tachograph) return true;

    return (
      this.timeSinceLastSignal() > tachograph.dailyDriving.effectiveRemaining
    );
  });

  protected hasOutdatedWeeklyDrivingData = computed(() => {
    const tachograph = this.driver().tachograph;
    if (!tachograph) return true;

    return (
      this.timeSinceLastSignal() > tachograph.weeklyDriving.effectiveRemaining
    );
  });

  public ngOnInit(): void {
    this.order$
      .pipe(
        tap(() => this.orderLoading.set(true)),
        switchMap(() => {
          const orderUuid = this.vehicle()?.orderUuid;
          if (!orderUuid) return of(null);

          return this.ordersService.getOrder(orderUuid).pipe(
            catchError((response) => {
              this.order.set(null);
              this.orderLoading.set(false);
              this.orderError.set(true);

              throw response;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((order) => {
        this.order.set(order);
        this.orderLoading.set(false);
        this.orderError.set(false);
      });

    effect(
      () => {
        this.vehicle();
        untracked(() => this.order$.next());
      },
      { injector: this.injector },
    );
  }

  protected addDelegation(): void {
    DelegationHelper.add(
      {
        vehicleUuid: this.vehicle()?.uuid,
        trailerUuid: this.vehicle()?.trailer?.uuid,
        primaryDriverUuid: this.driver().uuid,
      },
      this.router,
    );
  }

  protected assignVehicle(): void {
    this.modalService.open<DriverAssignedVehicleModalData>(
      DriverAssignedVehicleModalComponent,
      this.driver(),
    );
  }
}
